import axios from 'axios';
import { OPENAI_CONFIG, CONTENT_PROMPTS } from '../config/openai';
import type { ContentIdea } from '../stores/ideaStore';
import type { PlanAnswers } from '../components/dashboard/content/plan/types';
import { sanitizeHtml } from '../utils/sanitize';

interface OpenAIResponse {
  choices: Array<{
    message: {
      content: string;
    };
    index: number;
    finish_reason: string;
  }>;
}

interface NicheIdea {
  niche: string;
  description: string;
  emoji: string;
  contentIdeas: string[];
}

// Default fallback content ideas
const fallbackIdeas: ContentIdea[] = [
  {
    title: "Bastidores do Processo Criativo",
    description: "Mostre aos seus seguidores como você cria seu conteúdo, desde a ideação até o resultado final.",
    hashtags: ["BehindTheScenes", "ContentCreator", "CriarConteudo", "Criatividade", "ProcessoCriativo"],
    type: "post",
    category: "instagram"
  },
  {
    title: "Tutorial em Formato Carrossel",
    description: "Crie um guia passo a passo visual usando o formato carrossel do Instagram para ensinar algo valioso.",
    hashtags: ["Tutorial", "Aprendizagem", "DicasValiosas", "Carrossel", "ConteudoEducativo"],
    type: "post",
    category: "instagram"
  },
  {
    title: "Série 'Um Dia na Vida'",
    description: "Documente um dia típico mostrando sua rotina e compartilhando dicas práticas com seus seguidores.",
    hashtags: ["DayInTheLife", "Rotina", "Lifestyle", "BastidoresDaVida", "Dicas"],
    type: "post",
    category: "instagram"
  }
];

// Fallback ideas will be topic-specific now
const getFallbackIdeas = (topic: string): NicheIdea[] => {
  if (!topic) {
    return [
      {
        niche: "Criação de Conteúdo",
        description: "Compartilhe dicas e estratégias para criar conteúdo envolvente nas redes sociais.",
        emoji: "✨",
        contentIdeas: [
          "Como criar conteúdo viral",
          "Estratégias de engajamento",
          "Ferramentas essenciais para criadores"
        ]
      },
      {
        niche: "Lifestyle Digital",
        description: "Mostre como equilibrar vida pessoal e presença online.",
        emoji: "🌟",
        contentIdeas: [
          "Rotina de um criador de conteúdo",
          "Dicas de produtividade",
          "Bastidores da criação"
        ]
      },
      {
        niche: "Tendências Digitais",
        description: "Explore as últimas tendências e novidades do mundo digital.",
        emoji: "🚀",
        contentIdeas: [
          "Análise de tendências",
          "Novidades das redes sociais",
          "Previsões para o futuro"
        ]
      }
    ];
  }

  if (topic.toLowerCase().includes('fortnite')) {
    return [
      {
        niche: "História e Curiosidades de Fortnite",
        description: "Explore a história por trás do Fortnite e compartilhe curiosidades fascinantes sobre o jogo que os fãs adorariam saber.",
        emoji: "🎮",
        contentIdeas: [
          "Evolução do mapa do Fortnite ao longo das temporadas",
          "Eventos especiais mais memoráveis",
          "Teorias e segredos da história do jogo"
        ]
      },
      {
        niche: "Dicas e Truques para Se Tornar um Pro em Fortnite",
        description: "Forneça dicas valiosas, estratégias e truques para os jogadores melhorarem seu desempenho no Fortnite e atingirem o status de profissional.",
        emoji: "🏆",
        contentIdeas: [
          "Guias de construção avançada",
          "Estratégias de combate pro-level",
          "Configurações e dicas de performance"
        ]
      },
      {
        niche: "Bastidores da Criação de Fortnite",
        description: "Leve os espectadores para os bastidores da criação do Fortnite, mostrando o processo de desenvolvimento, a equipe por trás do jogo e os desafios enfrentados.",
        emoji: "⚡",
        contentIdeas: [
          "Como as skins são criadas",
          "Processo de desenvolvimento de novas temporadas",
          "Entrevistas com desenvolvedores"
        ]
      }
    ];
  }

  // Default fallback if topic doesn't match any specific category
  return [
    {
      niche: `${topic} para Iniciantes`,
      description: `Guia completo sobre ${topic} para quem está começando, com dicas práticas e conteúdo educativo.`,
      emoji: "🌟",
      contentIdeas: [
        `Guia básico de ${topic}`,
        `Dicas essenciais para iniciantes em ${topic}`,
        `Erros comuns a evitar em ${topic}`
      ]
    },
    {
      niche: `${topic} Avançado`,
      description: `Conteúdo aprofundado sobre ${topic} para quem busca conhecimento especializado.`,
      emoji: "🎯",
      contentIdeas: [
        `Técnicas avançadas de ${topic}`,
        `Análises detalhadas sobre ${topic}`,
        `Tendências e novidades em ${topic}`
      ]
    },
    {
      niche: `Lifestyle ${topic}`,
      description: `Compartilhe experiências e histórias relacionadas a ${topic} de uma perspectiva pessoal.`,
      emoji: "✨",
      contentIdeas: [
        `Um dia na vida de um entusiasta de ${topic}`,
        `Bastidores e experiências com ${topic}`,
        `Comunidade e eventos de ${topic}`
      ]
    }
  ];
};

// Validate OpenAI API key
const validateApiKey = () => {
  if (!OPENAI_CONFIG.apiKey) {
    throw new Error('OpenAI API key is missing. Please check your environment configuration.');
  }
  
  if (typeof OPENAI_CONFIG.apiKey !== 'string' || OPENAI_CONFIG.apiKey.trim() === '') {
    throw new Error('Invalid OpenAI API key format. Please check your environment configuration.');
  }
};

export const generateContentIdeas = async (): Promise<ContentIdea[]> => {
  try {
    // Validate API key before making the request
    validateApiKey();

    const response = await axios.post<OpenAIResponse>(OPENAI_CONFIG.endpoint, {
      model: OPENAI_CONFIG.model,
      messages: [
        {
          role: 'system',
          content: 'Você é um especialista em estratégia de conteúdo para redes sociais.'
        },
        {
          role: 'user',
          content: CONTENT_PROMPTS.ideas
        }
      ],
      max_tokens: OPENAI_CONFIG.maxTokens,
      temperature: OPENAI_CONFIG.temperature
    }, {
      headers: {
        'Authorization': `Bearer ${OPENAI_CONFIG.apiKey}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.data?.choices?.[0]?.message?.content) {
      console.warn('Invalid API response format, using fallback ideas');
      return fallbackIdeas;
    }

    try {
      const content = response.data.choices[0].message.content;
      const parsed = JSON.parse(content);
      
      if (!parsed.ideas || !Array.isArray(parsed.ideas) || parsed.ideas.length === 0) {
        console.warn('Invalid ideas format in API response, using fallback ideas');
        return fallbackIdeas;
      }

      return parsed.ideas.map((idea: any) => ({
        title: idea.title || 'Nova Ideia de Conteúdo',
        description: idea.description || 'Crie conteúdo envolvente para sua audiência',
        hashtags: Array.isArray(idea.hashtags) ? idea.hashtags : ['ContentCreator', 'NovaContent'],
        type: 'post',
        category: 'instagram'
      }));
    } catch (parseError) {
      console.warn('Error parsing API response:', parseError);
      return fallbackIdeas;
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      console.error('OpenAI API authentication failed. Please check your API key.');
      throw new Error('Falha na autenticação com a API do OpenAI. Por favor, verifique sua chave de API.');
    }
    console.error('Error generating content ideas:', error);
    return fallbackIdeas;
  }
};

export const generateNicheIdeas = async (topic: string): Promise<NicheIdea[]> => {
  try {
    // Validate API key before making the request
    validateApiKey();

    const prompt = `Generate 3 unique and creative content ideas for a social media channel about "${topic}". For each idea, provide:
    1. A specific niche title in Portuguese that is creative and catchy (not just a direct description, but a unique brand-like name)
    2. A brief, engaging description in Portuguese
    3. An appropriate emoji
    4. 3-5 unique content suggestions in Portuguese
    
    The ideas should be specifically focused on "${topic}" and provide valuable, engaging content suggestions.
    
    IMPORTANT: For the niche titles, be creative and come up with unique, brandable names - not just descriptive phrases. 
    For example, instead of "Restauração Clássica" for a classic car restoration channel, create something like "RetroRodas" or "AutoVintage".
    
    Format the response as JSON with this structure:
    {
      "ideas": [
        {
          "niche": "string",
          "description": "string",
          "emoji": "string",
          "contentIdeas": ["string"]
        }
      ]
    }`;

    const response = await axios.post<OpenAIResponse>(
      OPENAI_CONFIG.endpoint,
      {
        model: OPENAI_CONFIG.model,
        messages: [
          {
            role: 'system',
            content: 'You are a creative content strategy expert specializing in niche discovery and content ideation. Respond in Portuguese and ensure all suggestions are specifically related to the given topic. Create unique, brandable names for niches, not just descriptive titles.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: OPENAI_CONFIG.maxTokens,
        temperature: 0.9
      },
      {
        headers: {
          'Authorization': `Bearer ${OPENAI_CONFIG.apiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.data?.choices?.[0]?.message?.content) {
      console.warn('Invalid API response format, using fallback ideas');
      return getFallbackIdeas(topic);
    }

    try {
      const content = response.data.choices[0].message.content;
      const parsed = JSON.parse(content);

      if (!parsed.ideas || !Array.isArray(parsed.ideas) || parsed.ideas.length === 0) {
        console.warn('Invalid ideas format in API response, using fallback ideas');
        return getFallbackIdeas(topic);
      }

      return parsed.ideas;
    } catch (parseError) {
      console.warn('Error parsing API response:', parseError);
      return getFallbackIdeas(topic);
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      console.error('OpenAI API authentication failed. Please check your API key.');
      throw new Error('Falha na autenticação com a API do OpenAI. Por favor, verifique sua chave de API.');
    }
    console.error('Error generating niche ideas:', error);
    return getFallbackIdeas(topic);
  }
};

export const generateContentPlan = async (answers: PlanAnswers): Promise<string> => {
  try {
    // Validate API key before making the request
    validateApiKey();

    const response = await axios.post<OpenAIResponse>(OPENAI_CONFIG.endpoint, {
      model: OPENAI_CONFIG.model,
      messages: [
        {
          role: 'system',
          content: 'Você é um especialista em planejamento estratégico de conteúdo para redes sociais. Gere planos detalhados mantendo a estrutura HTML e classes CSS fornecidas.'
        },
        {
          role: 'user',
          content: CONTENT_PROMPTS.contentPlan(answers)
        }
      ],
      max_tokens: OPENAI_CONFIG.maxTokens,
      temperature: OPENAI_CONFIG.temperature
    }, {
      headers: {
        'Authorization': `Bearer ${OPENAI_CONFIG.apiKey}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.data?.choices?.[0]?.message?.content) {
      throw new Error('Resposta inválida da API');
    }

    const content = response.data.choices[0].message.content;
    return sanitizeHtml(content);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      console.error('OpenAI API authentication failed. Please check your API key.');
      throw new Error('Falha na autenticação com a API do OpenAI. Por favor, verifique sua chave de API.');
    }
    if (axios.isAxiosError(error)) {
      console.error('OpenAI API error:', error.response?.data);
      throw new Error(error.response?.data?.error?.message || 'Erro ao gerar plano de conteúdo');
    }
    console.error('Error generating content plan:', error);
    throw new Error('Falha ao gerar plano de conteúdo. Por favor, tente novamente.');
  }
};

export const analyzeScreenshot = async (base64Image: string): Promise<string> => {
  try {
    // Validate API key before making the request
    validateApiKey();

    const response = await axios.post<OpenAIResponse>(
      OPENAI_CONFIG.endpoint,
      {
        model: OPENAI_CONFIG.model,
        messages: [
          {
            role: 'system',
            content: 'You are an expert at analyzing social media analytics screenshots. Provide concise, actionable insights in Portuguese.'
          },
          {
            role: 'user',
            content: `Analyze this social media analytics screenshot and provide insights about the metrics shown. Base64 image: ${base64Image}`
          }
        ],
        max_tokens: OPENAI_CONFIG.maxTokens,
        temperature: 0.7
      },
      {
        headers: {
          'Authorization': `Bearer ${OPENAI_CONFIG.apiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.data?.choices?.[0]?.message?.content) {
      throw new Error('Resposta inválida da API');
    }

    return response.data.choices[0].message.content;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      console.error('OpenAI API authentication failed. Please check your API key.');
      throw new Error('Falha na autenticação com a API do OpenAI. Por favor, verifique sua chave de API.');
    }
    console.error('Error analyzing screenshot:', error);
    throw new Error('Falha ao analisar imagem. Por favor, tente novamente.');
  }
};

export const generateChannelName = async (niche: string, description: string): Promise<string> => {
  try {
    // Validate API key before making the request
    validateApiKey();

    const prompt = `Create a unique, catchy, and brandable name for a social media channel about "${niche}". 
    The channel focuses on: "${description}"
    
    The name should:
    1. Be memorable and easy to pronounce
    2. Not directly use the exact words from the niche (be creative)
    3. Have potential as a brand
    4. Work well with an @ symbol in front of it
    5. Not include spaces or special characters
    6. Be in Portuguese or work well internationally
    
    Return ONLY the name, without any explanation or additional text. Do not include the @ symbol.`;

    const response = await axios.post<OpenAIResponse>(
      OPENAI_CONFIG.endpoint,
      {
        model: OPENAI_CONFIG.model,
        messages: [
          {
            role: 'system',
            content: 'You are a creative branding expert who specializes in creating unique, memorable social media channel names.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 50,
        temperature: 0.9
      },
      {
        headers: {
          'Authorization': `Bearer ${OPENAI_CONFIG.apiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.data?.choices?.[0]?.message?.content) {
      return `${niche.toLowerCase().replace(/\s+/g, '')}official`;
    }

    // Clean up the response to ensure it's just the name
    const channelName = response.data.choices[0].message.content
      .trim()
      .replace(/^@/, '') // Remove @ if present
      .replace(/[^\w]/g, ''); // Remove any special characters or spaces

    return channelName || `${niche.toLowerCase().replace(/\s+/g, '')}official`;
  } catch (error) {
    console.error('Error generating channel name:', error);
    // Fallback to a simple name based on the niche
    return `${niche.toLowerCase().replace(/\s+/g, '')}official`;
  }
};